import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import type { ChangeEvent } from 'react';
import TextInput from 'components/Widget/Input/TextInput';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { GoogleFont } from 'dragonmouse-shared/interfaces';
import forge from 'apis/forge';
import * as S from './styles';

interface FontInputProps {
	font: string; // empty string means no font is selected
	onFontChange: (font: string) => void;
	onFontCommit?: (font: string) => void; // Called only when user selects from dropdown or clicks away
	onCancel?: () => void; // Called when ESC pressed with empty input
	autoFocus?: boolean;
}

const FontInput = ({ font, onFontChange, onFontCommit, onCancel, autoFocus }: FontInputProps) => {
	const [showFontList, setShowFontList] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [fonts, setFonts] = useState<GoogleFont[]>([]);
	const [filteredFonts, setFilteredFonts] = useState<GoogleFont[]>([]);
	const [activeIndex, setActiveIndex] = useState(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	useEffect(() => {
		const loadFonts = async () => {
			try {
				const googleFonts = await forge.getGoogleFonts();
				const sortedFonts = googleFonts.sort((a: any, b: any) => a.family.localeCompare(b.family));
				setFonts(sortedFonts);
				setFilteredFonts(sortedFonts);
			} catch (err) {
				console.error('Error loading fonts:', err);
			}
		};

		loadFonts();
	}, []);

	useEffect(() => {
		if (!searchQuery) {
			setFilteredFonts(fonts);
			return;
		}

		const filtered = fonts.filter((font) =>
			font.family.toLowerCase().includes(searchQuery.toLowerCase())
		);
		setFilteredFonts(filtered);
	}, [searchQuery, fonts]);

	// Reset active index when the filtered list changes
	useEffect(() => {
		setActiveIndex(-1);
	}, [filteredFonts]);

	// Scroll active item into view
	useEffect(() => {
		if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
			itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex]);

	// Only commit on click-outside if the user was actually interacting (dropdown open)
	useOnClickOutside(dropdownRef, () => {
		if (showFontList) {
			setShowFontList(false);
			if (onFontCommit) {
				onFontCommit(font);
			}
		}
	});

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onFontSelect = (selectedFont: GoogleFont) => {
		onFontChange(selectedFont.family);
		setShowFontList(false);
		setActiveIndex(-1);
		if (onFontCommit) {
			onFontCommit(selectedFont.family);
		}
	};

	const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
		onFontChange(value);
		setShowFontList(true);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (!showFontList && e.key !== 'Escape') {
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
				setShowFontList(true);
				if (filteredFonts.length > 0) {
					setActiveIndex(e.key === 'ArrowDown' ? 0 : filteredFonts.length - 1);
				}
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setActiveIndex((prev) => Math.min(prev + 1, filteredFonts.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setActiveIndex((prev) => Math.max(prev - 1, 0));
				break;
			case 'Enter':
				e.preventDefault();
				if (activeIndex >= 0 && filteredFonts[activeIndex]) {
					onFontSelect(filteredFonts[activeIndex]);
				}
				break;
			case 'Escape':
				e.preventDefault();
				setShowFontList(false);
				setActiveIndex(-1);
				if (!font && onCancel) {
					onCancel();
				} else if (onFontCommit) {
					onFontCommit(font);
				}
				break;
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.WrappedBase ref={dropdownRef}>
			<TextInput
				value={font}
				placeholder="Search Google Fonts..."
				onChange={onInputChange}
				onKeyDown={onKeyDown}
				onClick={() => {
					setShowFontList(true);
				}}
				onContextMenu={() => setShowFontList(false)}
				autoFocus={autoFocus}
			/>
			{showFontList && filteredFonts.length > 0 && (
				<S.FontList>
					{filteredFonts.map((fontItem, index) => (
						<S.FontItem
							key={fontItem.family}
							$active={index === activeIndex}
							ref={(el) => {
								itemRefs.current[index] = el;
							}}
							onClick={() => onFontSelect(fontItem)}
						>
							{fontItem.family}
						</S.FontItem>
					))}
				</S.FontList>
			)}
		</S.WrappedBase>
	);
};

export default FontInput;
